
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";

import {
  useAdminScope,
} from "../../../context/AdminScopeContext";

import {
  AdminButton,
} from "../../../pages/admin/action_engine";

import {
  AdminEmptyState,
  AdminErrorState,
  AdminLoadingState,
} from "../../../pages/admin/ui_engine";

import {
  createSupplierContact,
  getSupplierContactErrorMessage,
  listSupplierContacts,
  updateSupplierContact,
} from "../../../services/supplierContactsAdminApi";

import SupplierContactForm
  from "./SupplierContactForm";


function firstDefined(
  ...values
) {
  return values.find(
    (value) => (
      value !== undefined
      && value !== null
      && String(value).trim()
    ),
  );
}


function extractContacts(
  payload,
) {
  if (
    Array.isArray(payload)
  ) {
    return payload;
  }

  if (
    Array.isArray(
      payload?.items
    )
  ) {
    return payload.items;
  }

  if (
    Array.isArray(
      payload?.contacts
    )
  ) {
    return payload.contacts;
  }

  return [];
}


export default function SupplierContactsWorkspace({
  supplierUuid,
  supplierName = "",
}) {
  const scope =
    useAdminScope();

  const companyUuid =
    firstDefined(
      scope?.selectedCompanyUuid,
      scope?.companyUuid,
      scope?.selectedCompany?.uuid,
    );

  const activityUuid =
    firstDefined(
      scope?.selectedActivityUuid,
      scope?.activityUuid,
      scope?.selectedActivity?.uuid,
    );

  const [
    contacts,
    setContacts,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    editingContact,
    setEditingContact,
  ] = useState(null);

  const [
    creating,
    setCreating,
  ] = useState(false);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);


  const normalizedSupplierUuid =
    String(
      supplierUuid || ""
    ).trim();


  const loadContacts =
    useCallback(
      async () => {
        if (
          !normalizedSupplierUuid
          || !companyUuid
          || !activityUuid
        ) {
          setContacts([]);

          return;
        }

        setLoading(true);
        setErrorMessage("");

        try {
          const payload =
            await listSupplierContacts(
              normalizedSupplierUuid,
              {
                companyUuid,
                activityUuid,
              },
            );

          setContacts(
            extractContacts(
              payload,
            ),
          );

        } catch (error) {
          setContacts([]);

          setErrorMessage(
            getSupplierContactErrorMessage(
              error,
              "Impossible de charger "
              + "les contacts fournisseur.",
            ),
          );

        } finally {
          setLoading(false);
        }
      },
      [
        normalizedSupplierUuid,
        companyUuid,
        activityUuid,
      ],
    );


  useEffect(() => {
    setCreating(false);
    setEditingContact(null);

    loadContacts();

  }, [loadContacts]);


  const cancelForm = () => {
    setCreating(false);
    setEditingContact(null);
  };


  const createContact =
    async (payload) => {
      setSubmitting(true);
      setErrorMessage("");

      try {
        await createSupplierContact(
          normalizedSupplierUuid,
          payload,
          {
            companyUuid,
            activityUuid,
          },
        );

        cancelForm();

        await loadContacts();

      } catch (error) {
        setErrorMessage(
          getSupplierContactErrorMessage(
            error,
            "Impossible de créer "
            + "le contact fournisseur.",
          ),
        );

      } finally {
        setSubmitting(false);
      }
    };


  const updateContact =
    async (payload) => {
      if (
        !editingContact?.uuid
      ) {
        return;
      }

      setSubmitting(true);
      setErrorMessage("");

      try {
        await updateSupplierContact(
          normalizedSupplierUuid,
          editingContact.uuid,
          payload,
          {
            companyUuid,
            activityUuid,
          },
        );

        cancelForm();

        await loadContacts();

      } catch (error) {
        setErrorMessage(
          getSupplierContactErrorMessage(
            error,
            "Impossible de modifier "
            + "le contact fournisseur.",
          ),
        );

      } finally {
        setSubmitting(false);
      }
    };


  if (
    !normalizedSupplierUuid
  ) {
    return null;
  }


  return (
    <Root>
      <WorkspaceHeader>
        <div>
          <Eyebrow>
            Supplier Workspace
          </Eyebrow>

          <Title>
            Contacts
          </Title>

          {supplierName && (
            <Subtitle>
              {supplierName}
            </Subtitle>
          )}
        </div>

        <AdminButton
          type="button"
          variant="primary"
          disabled={
            !companyUuid
            || !activityUuid
            || submitting
          }
          onClick={() => {
            setEditingContact(
              null,
            );

            setCreating(
              true,
            );
          }}
        >
          Ajouter un contact
        </AdminButton>
      </WorkspaceHeader>


      {(
        !companyUuid
        || !activityUuid
      ) && (
        <AdminEmptyState
          title="Scope requis"
          message={
            "Sélectionnez une société "
            + "et une activité pour "
            + "gérer les contacts."
          }
          compact
        />
      )}


      {companyUuid
        && activityUuid
        && loading
        && (
          <AdminLoadingState
            message={
              "Chargement des "
              + "contacts fournisseur..."
            }
          />
        )}


      {errorMessage && (
        <AdminErrorState
          message={
            errorMessage
          }
          compact
        />
      )}


      {companyUuid
        && activityUuid
        && !loading
        && !errorMessage
        && contacts.length === 0
        && !creating
        && !editingContact
        && (
          <AdminEmptyState
            title={
              "Aucun contact fournisseur"
            }
            message={
              "Ajoutez un contact "
              + "commercial, commande, "
              + "ADV, support, technique "
              + "ou comptabilité."
            }
            compact
          />
        )}


      {companyUuid
        && activityUuid
        && !loading
        && contacts.length > 0
        && (
          <ContactGrid>
            {contacts.map(
              (contact) => (
                <ContactCard
                  key={
                    contact.uuid
                  }
                >
                  <CardHeader>
                    <div>
                      <ContactType>
                        {
                          contact.contact_type
                        }
                      </ContactType>

                      <ContactName>
                        {
                          contact.name
                        }
                      </ContactName>
                    </div>

                    <Badges>
                      {
                        contact.is_primary
                        && (
                          <Badge>
                            Principal
                          </Badge>
                        )
                      }

                      <Badge>
                        {
                          contact.is_active
                          !== false
                            ? "Actif"
                            : "Inactif"
                        }
                      </Badge>
                    </Badges>
                  </CardHeader>


                  <Details>
                    <span>
                      {
                        contact.email
                        || (
                          "E-mail "
                          + "non renseigné"
                        )
                      }
                    </span>

                    <span>
                      {
                        contact.phone
                        || (
                          "Téléphone "
                          + "non renseigné"
                        )
                      }
                    </span>

                    {contact.notes && (
                      <small>
                        {
                          contact.notes
                        }
                      </small>
                    )}
                  </Details>


                  <CardActions>
                    <AdminButton
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        setCreating(
                          false,
                        );

                        setEditingContact(
                          contact,
                        );
                      }}
                    >
                      Modifier
                    </AdminButton>
                  </CardActions>
                </ContactCard>
              ),
            )}
          </ContactGrid>
        )}


      {creating && (
        <SupplierContactForm
          submitting={
            submitting
          }
          onSubmit={
            createContact
          }
          onCancel={
            cancelForm
          }
        />
      )}


      {editingContact && (
        <SupplierContactForm
          contact={
            editingContact
          }
          submitting={
            submitting
          }
          onSubmit={
            updateContact
          }
          onCancel={
            cancelForm
          }
        />
      )}
    </Root>
  );
}


const Root = styled.section`
  display: grid;
  gap: 1rem;
`;

const WorkspaceHeader =
  styled.header`
    display: flex;
    align-items:
      flex-start;
    justify-content:
      space-between;
    gap: 1rem;
  `;

const Eyebrow = styled.p`
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  text-transform:
    uppercase;
  opacity: 0.65;
`;

const Title = styled.h3`
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0.3rem 0 0;
  opacity: 0.7;
`;

const ContactGrid =
  styled.div`
    display: grid;
    grid-template-columns:
      repeat(
        auto-fit,
        minmax(260px, 1fr)
      );
    gap: 1rem;
  `;

const ContactCard =
  styled.article`
    display: grid;
    gap: 0.8rem;
    padding: 1rem;
    border: 1px solid
      ${({ theme }) =>
        theme?.colors?.border
        || "#d8dde6"};
    border-radius: 10px;
  `;

const CardHeader =
  styled.header`
    display: flex;
    justify-content:
      space-between;
    gap: 0.75rem;
  `;

const ContactType =
  styled.small`
    font-weight: 700;
    opacity: 0.65;
  `;

const ContactName =
  styled.h4`
    margin: 0.2rem 0 0;
  `;

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const Badge = styled.span`
  padding:
    0.2rem 0.45rem;
  border:
    1px solid currentColor;
  border-radius: 999px;
  font-size: 0.75rem;
`;

const Details = styled.div`
  display: grid;
  gap: 0.3rem;
`;

const CardActions =
  styled.div`
    display: flex;
    justify-content:
      flex-end;
  `;
